import * as React from 'react';
import { useMergeRefs } from 'use-callback-ref';
import { WithCommonProps } from '@types';
import { PopoutPlacement } from '@hooks';
import { Button, ButtonProps } from '@components/button';
import { IconNames, IconSizes, Icon } from '@components/icon';
import { Tooltip, useHoverableTooltip } from '@components/tooltip';

export interface ButtonIconProps extends Omit<
  WithCommonProps<ButtonProps>,
  'children'
> {
  /** Name of the icon to be displayed */
  icon: IconNames;
  /** Size of the icon */
  iconSize?: IconSizes;
  /** Solid or outlined icon flag */
  iconSolid?: boolean;
  /** Content to be rendered inside the tooltip */
  children?: React.ReactNode;
  /** ariaDescribedby to reference the tooltip id by accessibility */
  ariaDescribedby?: string;
  /** Direct accessible name for icon-only buttons without a tooltip */
  ariaLabel?: string;
  /** Indicates the position of the popout */
  placement?: PopoutPlacement;
}

export const ButtonIcon = React.forwardRef(
  (
    props: ButtonIconProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => {
    const { triggerProps, opened } = useHoverableTooltip();
    const {
      icon,
      children,
      iconSize = 'lg',
      iconSolid = false,
      ariaDescribedby,
      ariaLabel,
      placement,
      variant,
      color,
      ...rest
    } = props;
    const [buttonElement, setButtonElement] =
      React.useState<HTMLElement | null>(null);
    const buttonRefCallback = React.useCallback(
      (el: HTMLButtonElement | null) => setButtonElement(el),
      []
    );
    const buttonRef = useMergeRefs<HTMLButtonElement>([buttonRefCallback, ref]);

    // Warn once in development when no accessible name is provided
    const didWarnRef = React.useRef(false);
    React.useEffect(() => {
      if (
        process.env.NODE_ENV === 'development' &&
        !ariaLabel &&
        !children &&
        !didWarnRef.current
      ) {
        didWarnRef.current = true;
        // eslint-disable-next-line no-console
        console.warn(
          'ButtonIcon: Missing ariaLabel. Icon-only buttons should have an ariaLabel for screen reader accessibility.'
        );
      }
    }, [ariaLabel, children]);

    // Compute variant: use 'outlined' if variant is 'outlined', otherwise use the provided variant
    const computedVariant = variant === 'outlined' ? 'outlined' : variant;

    // Compute color: if variant is 'outlined' and color is 'secondary', use 'default', otherwise use the provided color
    const computedColor =
      variant === 'outlined' && color === 'secondary' ? 'default' : color;

    const buttonProps = {
      ...triggerProps,
      ...rest,
      variant: computedVariant,
      color: computedColor,
      ref: buttonRef,
      ...(ariaDescribedby ? { 'aria-describedby': ariaDescribedby } : {}),
      ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
    } as ButtonProps & { ref: typeof buttonRef };

    return (
      <>
        <Button {...buttonProps}>
          <Icon name={icon} size={iconSize} solid={iconSolid} />
        </Button>

        {children && ariaDescribedby && (
          <Tooltip
            placement={placement}
            reference={buttonElement}
            id={ariaDescribedby}
            isOpen={opened}
          >
            {children}
          </Tooltip>
        )}
      </>
    );
  }
);

ButtonIcon.displayName = 'ButtonIcon';
